<template>
  <div class="row">
    <div class="medium-6 columns">
      <section>
        <h1 v-html="stepHeadline" />

        <!-- Hvor gammel er cyklen -->
        <article class="flow-group">
          <h3 v-html="bikeAgeHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="bikeAge"
              rules="required"
              :label="bikeAgeHeadline"
              name="bike-theft-claim-flow-bike-age"
            >
              <div class="row">
                <div class="columns medium-6">
                  <vue-select v-bind="field" :options="bikeAgeOptions" :placeholder="bikeAgeLabel" :model-value="bikeAge" />
                </div>
              </div>
              <ValidationError name="bike-theft-claim-flow-bike-age" />
            </Field>
          </div>
        </article>

        <!-- Har du en kvittering for din cykel -->
        <article class="flow-group">
          <h3 v-html="bikeReceiptHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="bikeReceipt"
              rules="required"
              :label="bikeReceiptHeadline"
              name="bike-theft-claim-flow-bike-receipt"
            >
              <base-radio-buttons-stacked v-bind="field" :options="bikeReceiptOptions" :model-value="bikeReceipt" />
              <ValidationError name="bike-theft-claim-flow-bike-receipt" />
            </Field>
            <info-box
              v-if="showBikeReceiptInfoBox"
              :text="bikeReceiptInfoBox"
              name="bike-theft-claim-flow-bike-receipt-info-box"
            />
            <div v-if="showUploadField">
              <h3 data-test-upload-bike-receipt-title v-html="bikeReceiptUploadSubHeadline" />

              <p data-test-upload-bike-receipt-text v-html="bikeReceiptText" />

              <base-file-field
                v-model="files"
                :title="bikeUploadedFilesLabel"
                data-vv-as="Kvitteringen"
                :required="showUploadField"
                name="bike-theft-claim-flow-bike-receipt-upload"
                button-class="text-center"
              />

              <p class="push-up" data-test-upload-file-info-text v-html="bikeReceiptUploadInfoBox" />
            </div>
          </div>
        </article>

        <!-- Hvad koster en tilsvarende cykel i dag -->
        <article class="flow-group">
          <h3 v-html="bikePriceHeadline" />
          <p v-html="bikePriceText" />
          <div class="control">
            <div class="row">
              <div class="columns medium-6">
                <Field
                  v-slot="{ field }"
                  v-model="bikePrice"
                  rules="required|numeric|min_value:500|max_value:60000"
                  :label="bikePriceHeadline"
                  name="bike-theft-claim-flow-bike-price"
                >
                  <base-text-input v-bind="field" />
                  <info-box :text="bikePriceInfoBox" />
                </Field>
              </div>
            </div>
            <ValidationError
              name="bike-theft-claim-flow-bike-price"
              :overrides="{
                numeric: bikePriceValidationTextOverrideNumeric,
                min_value: bikePriceValidationTextOverrideMinValue,
                max_value: bikePriceValidationTextOverrideMaxValue
              }"
            />
          </div>
        </article>

        <!-- Ekstraudstyr -->
        <article class="flow-group">
          <h3 v-html="extraBikeEquipmentHeadline" />
          <p v-html="extraBikeEquipmentText" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="extraBikeEquipment"
              rules="required"
              :label="extraBikeEquipmentHeadline"
              name="bike-theft-claim-flow-extra-bike-equipment"
            >
              <base-radio-buttons-stacked
                v-bind="field"
                :options="extraBikeEquipmentOptions"
                :model-value="extraBikeEquipment"
              />
              <ValidationError name="bike-theft-claim-flow-extra-bike-equipment" />
            </Field>
          </div>
        </article>

        <div class="row">
          <div class="columns medium-6">
            <router-link
              class="button secondary text-center"
              to="/opret-cykel-skade/what"
              data-test-bike-theft-claim-flow-link-button-back
            >
              Tilbage
            </router-link>
          </div>
          <div class="columns medium-6">
            <router-link class="button text-center" :to="routerLink" data-test-bike-theft-claim-flow-link-button-next>
              Videre
            </router-link>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import BaseRadioButtonsStacked from '@/components/Forms/BaseRadioButtonsStacked.vue';
import BaseTextInput from '@/components/Forms/BaseTextInput.vue';
import InfoBox from '@/components/UI/InfoBox.vue';
import Utility from '@/common/UtilityFunctions.js';
import BeforeRouteLeaveMixin from '../mixins/beforeRouteLeave.js';
import ValidationError from '@/components/UI/ValidationError.vue';
import { mapState } from 'vuex';

export default {
  name: 'Bike',
  components: {
    BaseRadioButtonsStacked,
    BaseTextInput,
    ValidationError,
    InfoBox
  },
  mixins: [BeforeRouteLeaveMixin],
  beforeRouteLeave(to, from, next) {
    this.leaveRoute(to, from, next);
  },
  computed: {
    ...mapState({
      bikeAgeHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeAgeHeadline,
      bikeAgeLabel: (state) => Utility.decodeHtml(state.bikeTheftClaimFlow.data.bikePage.bikeAgeLabel),
      bikeAgeOptions: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeAgeOptions,
      bikeFrameNumberHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeFrameNumberHeadline,
      bikeReceiptHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeReceiptHeadline,
      bikePriceHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.bikePriceHeadline,
      bikePriceInfoBox: (state) => state.bikeTheftClaimFlow.data.bikePage.bikePriceInfoBox,
      bikePriceText: (state) => state.bikeTheftClaimFlow.data.bikePage.bikePriceText,
      bikePriceValidationTextOverrideMaxValue: (state) =>
        state.bikeTheftClaimFlow.data.bikePage.bikePriceValidationTextOverrideMaxValue,
      bikePriceValidationTextOverrideMinValue: (state) =>
        state.bikeTheftClaimFlow.data.bikePage.bikePriceValidationTextOverrideMinValue,
      bikePriceValidationTextOverrideNumeric: (state) =>
        state.bikeTheftClaimFlow.data.bikePage.bikePriceValidationTextOverrideNumeric,
      bikeReceiptInfoBox: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeReceiptInfoBox,
      bikeReceiptOptions: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeReceiptOptions,
      bikeReceiptText: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeReceiptText,
      bikeReceiptUploadInfoBox: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeReceiptUploadInfoBox,
      bikeReceiptUploadSubHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeReceiptUploadSubHeadline,
      bikeUploadedFilesLabel: (state) => state.bikeTheftClaimFlow.data.bikePage.bikeUploadedFilesLabel,
      extraBikeEquipmentHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.extraBikeEquipmentHeadline,
      extraBikeEquipmentText: (state) => state.bikeTheftClaimFlow.data.bikePage.extraBikeEquipmentText,
      extraBikeEquipmentOptions: (state) => state.bikeTheftClaimFlow.data.bikePage.extraBikeEquipmentOptions,
      memberHasBikeFrameNumber: (state) => state.bikeTheftClaimFlow.data.bikePage.memberHasBikeFrameNumber,
      memberHasBikeFrameNumberInfoBox: (state) => state.bikeTheftClaimFlow.data.bikePage.memberHasBikeFrameNumberInfoBox,
      stepHeadline: (state) => state.bikeTheftClaimFlow.data.bikePage.stepHeadline
    }),
    bikeAge: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel.bikeAgeSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setBikeAge', value);
      }
    },
    bikeFrameNumber: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel.bikeFrameNumber;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setBikeFrameNumber', value);
      }
    },
    bikeReceipt: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel
          .bikeReceiptSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setBikeReceipt', value);
      }
    },
    bikePrice: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel.bikePrice;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setBikePrice', value);
      }
    },
    extraBikeEquipment: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel
          .extraBikeEquipmentSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setExtraBikeEquipment', value);
      }
    },
    files: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.uploadData.files;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setFiles', value);
      }
    },
    selectedHasBikeFrameNumber: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel
          .memberHasBikeFramSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setHasBikeFrameNumber', value);
      }
    },
    showUploadField() {
      return this.bikeReceipt === 'Ja';
    },
    showBikeReceiptInfoBox() {
      return this.bikeReceipt === 'Nej jeg har ingen kvittering';
    },
    routerLink() {
      if (this.extraBikeEquipment === 'Ja') {
        return '/opret-cykel-skade/what/bike/equipment';
      }

      return '/opret-cykel-skade/what/bike/summary';
    }
  },
  watch: {
    showUploadField() {
      this.$store.dispatch('bikeTheftClaimFlow/resetFiles');
    },
    files(value) {
      this.$store.dispatch('bikeTheftClaimFlow/setFiles', value);
    },
    extraBikeEquipment() {
      this.$store.dispatch('bikeTheftClaimFlow/resetEquipment');
    }
  }
};
</script>
